/**
 * EmployerRoleMatchesPage.js — /employer/roles/:id/matches  (RC 1.30.F).
 *
 * Review-only employer view. Consumes the RC 1.30.E endpoint
 *   GET /api/employer/roles/{id}/matches
 * and renders anonymous match intelligence cards.
 *
 * HARD RULES:
 *   • NO candidate identity. Matches are labelled "Match 001",
 *     "Match 002", …. No names, no avatars, no initials.
 *   • NO outreach affordance. No "Contact", "Message", "Reveal",
 *     no export. No bulk selection.
 *   • Sorting + filtering happen LOCALLY on the already-fetched
 *     payload — the backend endpoint already orders by
 *     fit_score desc nulls last.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Loader2, RefreshCw, AlertCircle, ChevronDown,
  Gauge, Sparkles, AlertTriangle, Info,
  Play, Send, UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';

import { apiFetch } from '@/lib/api';
import {
  anonLabel, formatFitPercent, sortAndFilterMatches,
  formatRelativeTime,
} from '@/pages/employerMatchesHelpers';
import InterestSignalComposer from '@/components/InterestSignalComposer';
import RouteToReviewersButton from '@/components/employer/roles/RouteToReviewersButton';
import { routeCandidate } from '@/lib/employerReviews';
import {
  listReviewerAssignments,
  listExistingReviewsForRoleCandidate,
} from '@/lib/employerReviewerRoster';
import { t, tn } from '@/lib/employerReviewerStrings';

// ─────────────────────────────────────────────────────────────
// Tone maps — P0 unified state-pill system.
// Decision pills (Surface/Hold/Pass) keep their existing visual
// language (accepted / pending / muted) but now reuse the shared
// state-pill classes so the whole product feels consistent.
// Signal-sent reuses pending tone; Access-granted reuses accepted
// tone — per P0 correction, NO separate signal-sent or access-
// granted tokens.
// ─────────────────────────────────────────────────────────────

const SIGNAL_TONE = {
  surface: 'state-pill state-pill--accepted',
  hold:    'state-pill state-pill--pending',
  pass:    'state-pill state-pill--muted',
};

const CONFIDENCE_TONE = 'state-pill state-pill--muted';

function fmtTs(iso) {
  return formatRelativeTime(iso);
}

function pct(score) {
  return formatFitPercent(score);
}

// Label index-based so a match is ANONYMOUS by construction.
// Implementation lives in the pure helper module so it's unit-tested.
function anonLabelLocal(i) {
  return anonLabel(i);
}


// ─────────────────────────────────────────────────────────────
// Match card (collapsed row + expandable drawer)
// ─────────────────────────────────────────────────────────────

export function MatchCard({
  match, index, signal, onSendInterest,
  /* loop-close v1 — route to reviewers (revealed matches only) */
  accountId, roleId, activeReviewerCount, onRouted,
}) {
  const [open, setOpen] = useState(false);
  const rationale = match.match_rationale_json || {};
  const strengths = rationale.strengths || [];
  const gaps      = rationale.gaps || [];
  const reasoning = rationale.reasoning || '';
  const isError   = Boolean(rationale.error);

  const label = anonLabelLocal(index);
  const signalTone = SIGNAL_TONE[match.decision_signal] || SIGNAL_TONE.pass;
  const confTone   = CONFIDENCE_TONE;
  const sent       = signal?.status === 'pending' || signal?.status === 'accepted' || signal?.status === 'declined' || signal?.status === 'deferred';

  // RC 1.30.I — reveal_state + revealed_profile drive the header.
  // Other states stay fully anonymous by construction (the backend
  // scrubs candidate_id for everything other than `accepted`).
  const isRevealed = match.reveal_state === 'revealed'
                  && match.revealed_profile;
  const displayHeadline = isRevealed
    ? match.revealed_profile.full_name
    : label;

  return (
    <Card
      className={`border-ndor-edge shadow-sm transition hover:border-ndor-ink-muted ${
        isRevealed ? 'state-rail--accepted' : ''
      }`}
      data-testid={`match-card-${match.id}`}
    >
      <CardContent className="p-5">
        {/* Collapsed row */}
        <div className="flex items-start gap-5">
          {/* Fit score */}
          <div className="flex flex-col items-center shrink-0 min-w-[72px]">
            <div
              className="text-2xl font-mono tabular-nums text-ndor-ink"
              data-testid={`match-fit-${match.id}`}
            >
              {pct(match.fit_score)}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted mt-0.5">
              Fit
            </div>
          </div>

          {/* Middle — label + signals */}
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-sm font-semibold text-ndor-ink ${
                  !isRevealed ? 'font-mono-ui tracking-wide' : ''
                }`}
                data-testid={`match-label-${match.id}`}
              >
                {displayHeadline}
              </span>
              {isRevealed && (
                <span
                  className="state-pill state-pill--accepted shrink-0"
                  data-testid={`match-access-granted-${match.id}`}
                >
                  Access granted
                </span>
              )}
              <span
                className={`${signalTone} capitalize shrink-0`}
                data-testid={`match-signal-${match.id}`}
              >
                {match.decision_signal}
              </span>
              <span
                className={`${confTone} capitalize shrink-0`}
                data-testid={`match-confidence-${match.id}`}
              >
                {match.confidence} confidence
              </span>
              {isError && (
                <span className="state-pill state-pill--declined shrink-0">
                  Requires retry
                </span>
              )}
              {sent && !isRevealed && (
                <span
                  className="state-pill state-pill--pending shrink-0"
                  data-testid={`match-signal-sent-${match.id}`}
                >
                  Signal sent
                </span>
              )}
            </div>

            {!open && reasoning && (
              <p className="text-xs text-ndor-ink-muted line-clamp-2">
                {reasoning}
              </p>
            )}

            <div className="flex items-center gap-3 text-[11px] text-ndor-ink-muted">
              <span>Surfaced {fmtTs(match.surfaced_at)}</span>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {!isError && !sent && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSendInterest && onSendInterest(match, label)}
                data-testid={`match-send-interest-${match.id}`}
              >
                <Send className="h-4 w-4 mr-1.5" /> Send interest
              </Button>
            )}
            {isRevealed && match.candidate_id && (
              <RouteToReviewersButton
                matchId={match.id}
                candidateId={match.candidate_id}
                accountId={accountId}
                roleId={roleId}
                activeReviewerCount={activeReviewerCount}
                onRouted={onRouted}
              />
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setOpen((o) => !o)}
              data-testid={`match-toggle-${match.id}`}
              aria-label={open ? 'Collapse match details' : 'Expand match details'}
              aria-expanded={open}
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-150 ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </Button>
          </div>
        </div>

        {/* Expanded panel */}
        {open && (
          <div
            className="mt-5 pt-5 border-t border-ndor-edge space-y-4"
            data-testid={`match-detail-${match.id}`}
          >
            {isRevealed && (
              <div
                className="rounded-md border px-4 py-3 space-y-2"
                style={{
                  backgroundColor: 'hsl(var(--ndor-state-accepted-bg))',
                  borderColor: 'hsl(var(--ndor-state-accepted-border))',
                }}
                data-testid={`match-revealed-profile-${match.id}`}
              >
                <div
                  className="flex items-center gap-2 text-[11px] uppercase tracking-wider"
                  style={{ color: 'hsl(var(--ndor-state-accepted-text))' }}
                >
                  <Sparkles className="h-3.5 w-3.5" /> Candidate profile — this role only
                </div>
                <div className="text-sm font-semibold text-ndor-ink">
                  {match.revealed_profile.full_name}
                </div>
                {match.revealed_profile.headline && (
                  <div className="text-xs text-ndor-ink-secondary">
                    {match.revealed_profile.headline}
                  </div>
                )}
                <div className="flex flex-wrap gap-3 text-[11px] text-ndor-ink-secondary">
                  {match.revealed_profile.current_job_title && (
                    <span>
                      <strong className="text-ndor-ink">Current:</strong>{' '}
                      {match.revealed_profile.current_job_title}
                      {match.revealed_profile.current_company
                        ? ` · ${match.revealed_profile.current_company}` : ''}
                    </span>
                  )}
                  {match.revealed_profile.location && (
                    <span>
                      <strong className="text-ndor-ink">Location:</strong>{' '}
                      {match.revealed_profile.location}
                    </span>
                  )}
                  {typeof match.revealed_profile.years_experience === 'number' && (
                    <span>
                      <strong className="text-ndor-ink">Experience:</strong>{' '}
                      {match.revealed_profile.years_experience} years of experience
                    </span>
                  )}
                </div>
                {match.revealed_profile.primary_domain && (
                  <div className="text-[11px] text-ndor-ink-secondary">
                    <strong className="text-[10px] uppercase tracking-wider text-ndor-ink-muted">
                      Primary domain
                    </strong>{' '}
                    <span className="ml-1">
                      {match.revealed_profile.primary_domain}
                    </span>
                  </div>
                )}
                {match.revealed_profile.summary && (
                  <p className="text-xs text-ndor-ink leading-relaxed whitespace-pre-wrap pt-1">
                    {match.revealed_profile.summary}
                  </p>
                )}
                {match.revealed_profile.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {match.revealed_profile.skills.slice(0, 20).map((s, i) => (
                      <span
                        key={i}
                        className="state-pill state-pill--muted"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-ndor-ink-muted pt-1">
                  This profile is scoped to this role and this match
                  only. No contact details, phone number, or CV file
                  have been shared.
                </p>
              </div>
            )}

            {reasoning && (
              <div>
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-ndor-ink-muted mb-1.5">
                  <Info className="h-3.5 w-3.5" /> Reasoning
                </div>
                <p
                  className="text-sm text-ndor-ink leading-relaxed"
                  data-testid={`match-reasoning-${match.id}`}
                >
                  {reasoning}
                </p>
              </div>
            )}

            {strengths.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-ndor-ink-muted mb-1.5">
                  <Sparkles className="h-3.5 w-3.5" /> Strengths
                </div>
                <ul
                  className="space-y-1.5 list-disc list-inside text-sm text-ndor-ink"
                  data-testid={`match-strengths-${match.id}`}
                >
                  {strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}

            {gaps.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-ndor-ink-muted mb-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Gaps
                </div>
                <ul
                  className="space-y-1.5 list-disc list-inside text-sm text-ndor-ink"
                  data-testid={`match-gaps-${match.id}`}
                >
                  {gaps.map((g, i) => <li key={i}>{g}</li>)}
                </ul>
              </div>
            )}

            {isError && (
              <div
                className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200"
                data-testid={`match-error-${match.id}`}
              >
                This match failed scoring ({rationale.error}).
                Re-run matching to attempt again.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────

const FILTERS = [
  { value: 'all',     label: 'All signals' },
  { value: 'surface', label: 'Surface only' },
  { value: 'hold',    label: 'Hold only' },
  { value: 'pass',    label: 'Pass only' },
];

const SORTS = [
  { value: 'fit_desc',  label: 'Fit score — high → low' },
  { value: 'fit_asc',   label: 'Fit score — low → high' },
  { value: 'recent',    label: 'Most recent' },
];

export default function EmployerRoleMatchesPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]   = useState(true);
  const [running, setRunning]   = useState(false);
  const [error, setError]       = useState(null);
  const [payload, setPayload]   = useState(null);
  const [filter, setFilter]     = useState('all');
  const [sort, setSort]         = useState('fit_desc');
  const [signalsByMatch, setSignalsByMatch] = useState({});
  const [composerMatch, setComposerMatch]   = useState(null);
  const [composerLabel, setComposerLabel]   = useState('');
  const [composerOpen, setComposerOpen]     = useState(false);

  // loop-close v1 — Part B: reviewer routing for revealed matches.
  // accountId comes from /api/employer/v1/me; activeReviewerCount is
  // derived from emp_reviewer_assignments scoped to this role.
  // Both are best-effort; failures degrade to a disabled button.
  const [accountId, setAccountId] = useState(null);
  const [activeReviewerCount, setActiveReviewerCount] = useState(0);

  const loadReviewerContext = useCallback(async () => {
    try {
      const me  = await apiFetch('/api/employer/v1/me');
      const aid = me?.organisation?.id ?? me?.account_id ?? null;
      setAccountId(aid);
      if (!aid) { setActiveReviewerCount(0); return; }
      const assignments = await listReviewerAssignments(aid);
      const n = (assignments || []).filter(
        (a) => a.role_id === id && a.assignment_status === 'active',
      ).length;
      setActiveReviewerCount(n);
    } catch {
      // Soft feature — disable routing if context fetch fails.
      setAccountId(null);
      setActiveReviewerCount(0);
    }
  }, [id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/api/employer/roles/${id}/matches?limit=200`);
      setPayload(data || null);
    } catch (err) {
      setError(err?.data?.detail || err?.message || 'Failed to load matches.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadSignals = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/employer/roles/${id}/signals`);
      setSignalsByMatch(data?.signals_by_match || {});
    } catch {
      // Soft feature — do not block the matches view on a signals
      // fetch failure. Empty map = "no prior signals" which is a
      // correct default.
      setSignalsByMatch({});
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSignals(); }, [loadSignals]);
  useEffect(() => { loadReviewerContext(); }, [loadReviewerContext]);

  const onSendInterest = (match, label) => {
    setComposerMatch(match);
    setComposerLabel(label);
    setComposerOpen(true);
  };

  const onRunMatching = async () => {
    if (running) return;
    setRunning(true);
    try {
      await apiFetch(`/api/employer/roles/${id}/run-matching`, {
        method: 'POST',
      });
      toast.success('Matching scheduled. Refreshing in a moment…');
      // Poll once after a short delay so the first batch of rows
      // shows up without the user having to click Refresh.
      setTimeout(() => { load(); }, 12_000);
    } catch (err) {
      toast.error(err?.data?.detail || err?.message || 'Could not schedule matching.');
    } finally {
      setRunning(false);
    }
  };

  const role    = payload?.role || null;
  const matches = payload?.matches || [];
  const counts  = payload?.counts || { surface: 0, hold: 0, pass: 0, total: 0 };

  const filtered = useMemo(
    () => sortAndFilterMatches(matches, filter, sort),
    [matches, filter, sort],
  );

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-24"
        data-testid="employer-matches-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="mx-auto max-w-xl py-24 px-4 text-center"
        data-testid="employer-matches-error"
      >
        <AlertCircle className="mx-auto mb-4 h-10 w-10 text-rose-500" />
        <p className="text-sm text-ndor-ink-muted">{error}</p>
        <Button
          variant="outline" className="mt-6"
          onClick={() => navigate('/employer/roles')}
        >
          Back to roles
        </Button>
      </div>
    );
  }

  const isDraft = role?.status === 'draft';
  const noMatches = matches.length === 0;

  return (
    <div
      className="mx-auto max-w-4xl py-8 px-4 space-y-6"
      data-testid="employer-matches-page"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate(`/employer/roles/${id}`)}
            className="mb-2"
            data-testid="employer-matches-back"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Role editor
          </Button>
          <h1 className="text-2xl font-semibold text-ndor-ink flex items-center gap-2">
            <Gauge className="h-5 w-5 text-ndor-ink-muted" />
            {role?.title || 'Role matches'}
          </h1>
          <p className="mt-1 text-sm text-ndor-ink-muted">
            Anonymous match intelligence for discoverable candidates.
            No candidate identity is surfaced.
          </p>
        </div>
        <span
          className={`state-pill ${
            role?.status === 'live'
              ? 'state-pill--accepted'
              : role?.status === 'draft'
                ? 'state-pill--draft'
                : 'state-pill--muted'
          }`}
          data-testid="employer-matches-role-status"
        >
          {role?.status || 'draft'}
        </span>
      </div>

      {/* Draft banner — publish before matching runs */}
      {isDraft && (
        <Card
          className="border-amber-500/40 bg-amber-500/10 shadow-none"
          data-testid="employer-matches-draft-banner"
        >
          <CardContent className="py-4">
            <p className="text-sm text-amber-200">
              This role is still a draft. Matching runs automatically
              once the role is published.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Counts strip */}
      {!isDraft && (
        <div
          className="grid grid-cols-4 gap-3"
          data-testid="employer-matches-counts"
        >
          <CountTile label="Total"    value={counts.total}    testId="count-total" />
          <CountTile label="Surface"  value={counts.surface}  testId="count-surface" tone="emerald" />
          <CountTile label="Hold"     value={counts.hold}     testId="count-hold"    tone="amber" />
          <CountTile label="Pass"     value={counts.pass}     testId="count-pass"    tone="slate" />
        </div>
      )}

      {/* Toolbar */}
      {!isDraft && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="matches-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTERS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-[220px]" data-testid="matches-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline"
              onClick={load}
              data-testid="matches-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
            </Button>
            <Button
              size="sm"
              onClick={onRunMatching}
              disabled={running || role?.status !== 'live'}
              data-testid="matches-run"
              className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
            >
              {running
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <Play className="h-4 w-4 mr-1.5" />}
              {running ? 'Running…' : 'Run matching'}
            </Button>
          </div>
        </div>
      )}

      {/* Cards */}
      {!isDraft && noMatches && (
        <Card
          className="border-ndor-edge shadow-sm"
          data-testid="employer-matches-empty"
        >
          <CardContent className="py-12 text-center max-w-md mx-auto">
            <Gauge className="mx-auto mb-3 h-8 w-8 text-ndor-ink-muted" />
            <p className="text-sm font-medium text-ndor-ink">
              No matches yet for this role.
            </p>
            <p className="text-xs text-ndor-ink-muted mt-2 leading-relaxed">
              If matching has just run, results may take a moment to
              surface. Otherwise, click <strong>Run matching</strong>{' '}
              above to score the eligible candidates against this
              role's criteria. If no eligible candidates are surfaced
              under the current constraints, this list will stay
              empty until either the role is re-scoped or new
              candidates onboard.
            </p>
            <p className="text-[11px] text-ndor-ink-muted mt-3 leading-relaxed">
              <strong>What happens next:</strong> matches appear here
              anonymously. Send an interest signal to a candidate; if
              they accept, their identity reveals and you can route
              them to your reviewers from this same surface.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <Button
                asChild
                size="sm"
                variant="outline"
                data-testid="employer-matches-empty-cta-detail"
              >
                <Link to={`/employer/roles/${id}`}>
                  Open role detail
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!isDraft && !noMatches && (
        <div
          className="space-y-3"
          data-testid="employer-matches-list"
        >
          {/* P1.5 — Hold filter context note. Clarifies the
              decision category without introducing a 4th taxonomy
              tier. Only rendered when the Hold filter is active. */}
          {filter === 'hold' && (
            <div
              className="rounded-md border border-ndor-edge bg-ndor-surface-alt px-3 py-2"
              data-testid="employer-matches-hold-note"
            >
              <div className="flex items-start gap-2 text-xs text-ndor-ink-secondary leading-relaxed">
                <Info className="h-3.5 w-3.5 mt-0.5 text-ndor-ink-muted shrink-0" />
                <span>
                  <strong className="text-ndor-ink">On hold:</strong>{' '}
                  matches you're not ready to action yet. These remain
                  in the queue.
                </span>
              </div>
            </div>
          )}

          {filtered.length === 0 ? (
            <Card
              className="border-ndor-edge shadow-sm"
              data-testid="employer-matches-filter-empty"
            >
              <CardContent className="py-10 text-center">
                <p className="text-sm text-ndor-ink-muted">
                  No matches under the current filter.
                </p>
              </CardContent>
            </Card>
          ) : (
            filtered.map((m, i) => (
              <MatchCard
                key={m.id}
                match={m}
                index={i}
                signal={signalsByMatch[m.id]}
                onSendInterest={onSendInterest}
                accountId={accountId}
                roleId={id}
                activeReviewerCount={activeReviewerCount}
                onRouted={loadReviewerContext}
              />
            ))
          )}
        </div>
      )}

      {/* Review-only footer */}
      {!isDraft && (
        <p
          className="text-[11px] text-ndor-ink-muted text-center pt-4"
          data-testid="employer-matches-review-only-note"
        >
          Candidate identity remains private until they accept an
          interest signal. Reveal is scoped to that specific role and
          that specific match.
        </p>
      )}

      <InterestSignalComposer
        roleId={id}
        match={composerMatch}
        matchLabel={composerLabel}
        open={composerOpen}
        onOpenChange={setComposerOpen}
        onSent={() => loadSignals()}
      />
    </div>
  );
}


function CountTile({ label, value, testId, tone }) {
  const toneClass = {
    emerald: 'text-emerald-200',
    amber:   'text-amber-200',
    slate:   'text-slate-200',
  }[tone] || 'text-ndor-ink';
  return (
    <div
      className="rounded-lg border border-ndor-edge bg-ndor-surface-alt px-4 py-3"
      data-testid={testId}
    >
      <div className={`text-xl font-mono tabular-nums ${toneClass}`}>
        {value ?? 0}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-ndor-ink-muted mt-0.5">
        {label}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// RouteToReviewersButton — extracted to
// `components/employer/roles/RouteToReviewersButton.jsx` for reuse
// across the legacy matches page AND the Channel-2 Surfaced
// Candidates panel (RC 1.62 Channel-2 Lifecycle Completion).
// ─────────────────────────────────────────────────────────────
